// src/components/pages/Checkout.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Faqs from "../sections/Faqs";
import { useTranslation } from "react-i18next";

/* Axios/CSRF */
axios
  .get("https://laravel-api.aj-mart.com/csrf-token")
  .then((res) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = res.data.csrf_token;
  })
  .catch(() => {});
axios.defaults.withCredentials = true;

/* ثابت الدليفري الافتراضي */
const DEFAULT_DELIVERY = 1;

/* Helpers */
const toMoney = (n) => Number(n || 0).toFixed(2);
const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
const isHexString = (s) =>
  typeof s === "string" && /^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(s?.trim?.() || "");

const toNum = (v, d = 0) => {
  const n = Number.parseFloat(String(v ?? "").toString().replace(",", "."));
  return Number.isFinite(n) ? n : d;
};
const firstFromMaybeJson = (v) => {
  if (!v) return null;
  if (Array.isArray(v)) return v[0] ?? null;
  if (typeof v === "string") {
    try {
      const arr = JSON.parse(v);
      if (Array.isArray(arr)) return arr[0] ?? null;
    } catch {}
    if (v.includes(",")) return v.split(",")[0]?.trim() || null;
  }
  return null;
};

const normalizeCapKey = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
// eslint-disable-next-line no-unused-vars
const normalizeCapacity = (s) => (typeof s === "string" ? s.replace(/\s+/g, "") : s);

/* ======= Color name resolution (name only) ======= */
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  if (/^(rgb|hsl)a?\(/i.test(s)) return s;
  if (/^[a-z]+$/i.test(s)) return s;
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`;
  return "";
};
const toArrayObjects = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === "object") return Object.values(val);
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      if (parsed && typeof parsed === "object") return Object.values(parsed);
    } catch {}
  }
  return [];
};
const getColorValue = (item) =>
  item?.selected_color ??
  item?.color_hex ??
  item?.product_color_hex ??
  item?.variation_color_hex ??
  item?.accessories_color_hex ??
  item?.item_data?.selected_color ??
  null;
const directColorName = (item) => {
  let n =
    item?.selected_color_name ??
    item?.color_name ??
    item?.variation_color_name ??
    item?.accessories_color_name ??
    item?.item_data?.selected_color_name ??
    null;
  if (typeof n === "string" && n.includes(",")) n = n.split(",")[0].trim();
  return n || null;
};
const colorNameFromMap = (item) => {
  const mapArr = [
    ...toArrayObjects(item?.product_colors_with_hex),
    ...toArrayObjects(item?.item_data?.product_colors_with_hex),
  ];
  if (!mapArr.length) return null;
  const val = sanitizeCssColor(getColorValue(item) || "");
  if (!val) return null;
  const needle = val.replace(/^#/, "").toLowerCase();
  for (const c of mapArr) {
    const hex = sanitizeCssColor(c?.hex || c?.color_hex || "").replace(/^#/, "").toLowerCase();
    if (hex && hex === needle) {
      const name = (c?.name || c?.label || c?.title || c?.color_name || "").trim();
      if (name) return name;
    }
  }
  return null;
};
const fallbackColorName = (item) => {
  const sel = item?.selected_color ?? item?.item_data?.selected_color ?? null;
  if (sel && !isHexString(sel)) return String(sel).split(",")[0].trim();
  const pc =
    (typeof item?.product_color === "string" ? item.product_color : null) ??
    (typeof item?.item_data?.product_color === "string" ? item.item_data.product_color : null) ??
    null;
  if (pc && !isHexString(pc)) {
    return pc.includes(",") ? pc.split(",")[0].trim() : pc.trim();
  }
  const acc = (() => {
    const v = item?.accessories_color;
    if (!v) return null;
    if (Array.isArray(v)) return v[0] ?? null;
    if (typeof v === "string") {
      try {
        const arr = JSON.parse(v);
        if (Array.isArray(arr)) return arr[0] ?? null;
      } catch {}
      if (v.includes(",")) return v.split(",")[0].trim();
      return v;
    }
    return null;
  })();
  if (acc && !isHexString(acc)) return acc;
  return null;
};
const getColorName = (item) => {
  const direct = directColorName(item);
  if (direct) return direct;
  const fromMap = colorNameFromMap(item);
  if (fromMap) return fromMap;
  const fb = fallbackColorName(item);
  if (fb) return fb;
  return null;
};

const getCapacity = (item) =>
  item?.selected_capacity ??
  item?.capacity ??
  item?.product_capacity_selected ??
  item?.variation_capacity ??
  null;

/* ===== capacity pricing helpers ===== */
const getCapacityPrices = (item) => {
  let p =
    item?.product_capacity_prices ??
    item?.item_data?.product_capacity_prices ??
    item?.product?.product_capacity_prices ??
    null;
  if (!p) return null;
  if (typeof p === "string") {
    try {
      const obj = JSON.parse(p);
      return obj && typeof obj === "object" ? obj : null;
    } catch {
      return null;
    }
  }
  return typeof p === "object" ? p : null;
};

const getCapOverridePrice = (item) => {
  const cap = getCapacity(item);
  const prices = getCapacityPrices(item);
  if (!cap || !prices) return null;
  const target = normalizeCapKey(cap);
  for (const [k, v] of Object.entries(prices)) {
    if (normalizeCapKey(k) === target) {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
};

const getDiscountPercent = (item) => {
  const raw = item?.product_price_discount ?? item?.accessories_price_discount ?? 0;
  const n = Number.parseFloat(raw) || 0;
  return n > 0 ? n : 0;
};

/** يحسب سعر الوحدة مع مراعاة الـ capacity لو عندنا خريطة أسعار */
const computeUnitPrice = (item) => {
  const isProduct = !!item.product_id;
  const capPrice = isProduct ? getCapOverridePrice(item) : null;
  let base = isProduct
    ? (capPrice ?? Number(item.product_price ?? item.price ?? 0))
    : Number(item.accessories_price ?? item.price ?? 0);
  base = Number.isFinite(base) ? base : 0;
  const discPct = getDiscountPercent(item);
  const after = base; // هنا ما بنخصمش (الإجمالي كان منفصل عندك)
  return {
    unit: Number(after.toFixed(2)),
    base: Number(base.toFixed(2)),
    discount_percent: discPct,
    used_capacity_price: capPrice != null,
  };
};

/** محاولة جلب خريطة أسعار السعات لمَن لا يملكها (best-effort) */
const enrichMissingCapacityMaps = async (items) => {
  const out = [...items];
  await Promise.all(
    out.map(async (it, idx) => {
      if (!it?.product_id) return;
      const hasMap = !!getCapacityPrices(it);
      if (hasMap) return;
      const candidates = [
        `https://laravel-api.aj-mart.com/api/products/${it.product_id}`,
        `https://laravel-api.aj-mart.com/api/product/${it.product_id}`,
        `https://laravel-api.aj-mart.com/api/product/show/${it.product_id}`,
      ];
      for (const url of candidates) {
        try {
          const res = await axios.get(url, { withCredentials: true });
          const prod =
            res.data?.data?.product ??
            res.data?.product ??
            res.data?.data ??
            null;
          const map =
            prod?.product_capacity_prices ??
            prod?.capacity_prices ??
            null;
          if (map) {
            out[idx] = { ...it, product_capacity_prices: map };
            break;
          }
        } catch {}
      }
    })
  );
  return out;
};

/* يحضّر احتمالات مختلفة لمفتاح السلّة */
const cartKeyVariants = (key) => {
  const variants = new Set();
  const raw = String(key || "");
  if (!raw) return [];
  variants.add(raw);
  variants.add(encodeURIComponent(raw));
  const parts = raw.split("_");
  if (parts.length >= 3) {
    const head = parts.slice(0, 2).join("_");
    const tail = parts.slice(2).join("_");
    const noSpaceTail = tail.replace(/\s+/g, "");
    const collapsed = `${head}_${noSpaceTail}`;
    variants.add(collapsed);
    variants.add(encodeURIComponent(collapsed));
  }
  return [...variants];
};

/* --- Models helpers (CSV / JSON / Array) --- */
const toArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {}
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// eslint-disable-next-line no-unused-vars
const getModels = (item) =>
  toArray(
    item?.selected_models ??
    item?.accessories_selected_models ??
    item?.models ??
    item?.selected_model ??
    item?.item_data?.selected_models ??
    null
  );

export default function Checkout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // state -> localStorage -> API (/cart)
  const [ctx, setCtx] = useState(() => {
    if (location.state?.cartItems?.length) {
      return { ...location.state, delivery: toNum(location.state.delivery, DEFAULT_DELIVERY) };
    }
    try {
      const raw = localStorage.getItem("aj_checkout_snapshot");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.cartItems?.length) {
          return { ...parsed, delivery: toNum(parsed.delivery, DEFAULT_DELIVERY) };
        }
      }
    } catch {}
    return {
      cartId: null,
      cartItems: [],
      subtotal: 0,
      delivery: DEFAULT_DELIVERY,
      discountPercent: 0,
      discountAmount: 0,
      total: 0,
      couponCode: null,
      couponId: null,
    };
  });

  useEffect(() => {
    const fetchIfEmpty = async () => {
      if (ctx.cartItems?.length) return;
      try {
        const res = await axios.get("https://laravel-api.aj-mart.com/cart", { withCredentials: true });
        const cart = res.data.cart || {};
        const cartItems = Object.keys(cart).map((key) => ({ ...cart[key], cart_key: key }));

        const subtotal = cartItems.reduce((acc, it) => {
          const { unit } = computeUnitPrice(it);
          return acc + unit * (Number(it.quantity) || 0);
        }, 0);

        const delivery =
          toNum(res.data?.delivery, NaN);
        const deliveryFixed = Number.isFinite(delivery) && delivery > 0 ? delivery : DEFAULT_DELIVERY;

        const discountPercent = Number(res.data?.discountPercent || 0);
        const discountAmount = Number(res.data?.discountAmount || 0);
        const couponCode = res.data?.couponCode || null;
        const couponId = res.data?.couponId || null;

        const cartId =
          res.data?.cart_id ||
          res.data?.id ||
          localStorage.getItem("aj_cart_id") ||
          null;

        const total = Number(subtotal - discountAmount + deliveryFixed).toFixed(2);
        const snapshot = {
          cartId,
          cartItems,
          subtotal,
          delivery: deliveryFixed,
          discountPercent,
          discountAmount,
          total,
          couponCode,
          couponId,
          models: [],
        };
        setCtx(snapshot);
        localStorage.setItem("aj_checkout_snapshot", JSON.stringify(snapshot));
      } catch {}
    };
    fetchIfEmpty();
  }, [ctx.cartItems]);

  // UI state
  const [selectedOption, setSelectedOption] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    apartment_details: "",
    order_notes: "",
  });
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  // eslint-disable-next-line no-unused-vars
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);

  const handleRadioChange = (e) => setSelectedOption(e.target.value);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const deliveryVal = useMemo(() => {
    let snapshotDelivery = DEFAULT_DELIVERY;
    try {
      const snap = JSON.parse(localStorage.getItem("aj_checkout_snapshot") || "{}");
      snapshotDelivery = toNum(snap?.delivery, DEFAULT_DELIVERY);
    } catch {}
    return toNum(ctx.delivery, snapshotDelivery || DEFAULT_DELIVERY);
  }, [ctx.delivery]);

  const computedSubtotal = useMemo(() => {
    return (ctx.cartItems || []).reduce((acc, it) => {
      const { unit } = computeUnitPrice(it);
      return acc + unit * (Number(it.quantity) || 0);
    }, 0);
  }, [ctx.cartItems]);

  const computedTotal = useMemo(() => {
    return Number(
      computedSubtotal - Number(ctx.discountAmount || 0) + Number(deliveryVal || 0)
    ).toFixed(2);
  }, [computedSubtotal, ctx.discountAmount, deliveryVal]);

  const total = computedTotal;

  const { t: _t } = useTranslation();
  const setPopup = (msg, type = "success", ms = 2500) => {
    const txt = typeof msg === "string" ? msg : "Done";
    setPopupMessage(_t(txt));
    setPopupType(type);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), ms);
  };

  const validateForm = () => {
    if (!formData.full_name || !formData.phone || !formData.address || !formData.city) {
      setPopup("يرجى تعبئة الحقول المطلوبة", "error");
      return false;
    }
    if (!selectedOption) {
      setPopup("Please select a payment method", "error");
      return false;
    }
    if (!ctx.cartItems?.length) {
      setPopup("Your cart is empty", "error");
      return false;
    }
    return true;
  };

  /* حذف صارم لكل عناصر السلة على السيرفر */
  const clearServerCart = async () => {
    try {
      for (let pass = 0; pass < 3; pass++) {
        const res = await axios.get("https://laravel-api.aj-mart.com/cart", { withCredentials: true });
        const cart = res.data?.cart || {};
        const keys = Object.keys(cart);
        if (!keys.length) break;

        for (const key of keys) {
          const variants = cartKeyVariants(key);
          for (const v of variants) {
            try {
              await axios.delete(`https://laravel-api.aj-mart.com/cart/${v}`, { withCredentials: true });
              break;
            } catch {}
          }
        }
        await new Promise((r) => setTimeout(r, 200));
      }
    } catch {}
  };

  /** Place Order */
  const handlePlaceOrder = async () => {
    if (!validateForm()) return;

    setIsPlacing(true);

    const snap = (() => {
      try { return JSON.parse(localStorage.getItem("aj_checkout_snapshot") || "{}"); }
      catch { return {}; }
    })();

    const couponCode = ctx.couponCode ?? snap.couponCode ?? null;
    const couponId   = ctx.couponId   ?? snap.couponId   ?? null;
    const cartId     = ctx.cartId     ?? snap.cartId     ?? null;

    const originalItems = Array.isArray(ctx.cartItems) && ctx.cartItems.length
      ? ctx.cartItems
      : Array.isArray(snap.cartItems) ? snap.cartItems : [];

    const itemsSrc = await enrichMissingCapacityMaps(originalItems);

    const recomputedSubtotal = itemsSrc.reduce((acc, it) => {
      const { unit } = computeUnitPrice(it);
      return acc + unit * (toNum(it.quantity) || 0);
    }, 0);

    const subtotal = toNum(snap.subtotal, recomputedSubtotal);
    const deliveryNum = toNum(snap.delivery, toNum(ctx.delivery, DEFAULT_DELIVERY));
    const discountPercent = toNum(snap.discountPercent, toNum(ctx.discountPercent, 0));
    const discountAmount  = toNum(snap.discountAmount,  toNum(ctx.discountAmount,  0));
    const totalVal = subtotal - discountAmount + deliveryNum;
    const totalNum = Number(totalVal.toFixed ? totalVal.toFixed(2) : toNum(totalVal).toFixed(2));

    const itemsPayload = itemsSrc.map((item) => {
      const isProduct  = !!item.product_id;
      const colorName  = getColorName(item);
      const colorVal   = getColorValue(item);
      const capacity   = getCapacity(item);
      const capNoSpace = capacity ? capacity.replace(/\s+/g, "") : null;

      const capPrices  = getCapacityPrices(item);
      const { unit, base, discount_percent } = computeUnitPrice(item);

      const modelsArr  = (() => {
        if (Array.isArray(item?.selected_models)) return item.selected_models;
        if (Array.isArray(item?.item_data?.selected_models)) return item.item_data.selected_models;
        const csv = item?.models ?? item?.item_data?.models ?? null;
        if (!csv) return [];
        if (Array.isArray(csv)) return csv;
        if (typeof csv === "string") {
          try {
            const j = JSON.parse(csv);
            if (Array.isArray(j)) return j;
          } catch {}
          return csv.split(",").map(s => s.trim()).filter(Boolean);
        }
        return [];
      })();
      const modelLabel = modelsArr.length ? modelsArr.join(", ") : null;

      const baseObj = {
        item_type: isProduct ? "product" : "accessories",
        quantity: toNum(item.quantity, 1),

        color: colorName || colorVal || null,
        capacity: capacity || null,
        variant_color: colorName || colorVal || null,
        variant_capacity: capNoSpace || null,

        model_name: modelLabel, // ← الاسم الموحّد

        unit_price: unit,
        price: unit,
        price_original: base,
        discount_percent,

        item_data: {
          selected_color: colorVal || colorName || firstFromMaybeJson(item?.accessories_color) || null,
          selected_color_name: colorName || null,
          selected_capacity: capacity || null,
          selected_models: modelsArr.length ? modelsArr : null,

          product_price: unit,
          product_price_original: base,
          product_price_discount: discount_percent,
          product_capacity_prices: capPrices || null,

          // نمرّر الماب عشان backend/ملفات مستقبلية لو احتجناه
          product_colors_with_hex: item?.product_colors_with_hex || item?.item_data?.product_colors_with_hex || null,

          product_name_E: isProduct ? item.product_name_E : item.accessories_name_E,
          product_main_img: isProduct ? item.product_main_img : item.accessories_main_img,

          coupon_code: couponCode ?? null,
          coupon_id:   couponId   ?? null,
          cart_id:     cartId     ?? null,
        },
      };

      return isProduct
        ? {
            ...baseObj,
            product_id: item.product_id,
            name: item.product_name_E || item.product_name_A || null,
            image: item.product_main_img || null,
          }
        : {
            ...baseObj,
            accessories_id: item.accessories_id,
            name: item.accessories_name_E || item.accessories_name_A || null,
            image: item.accessories_main_img || null,
          };
    });

    const orderData = {
      full_name: formData.full_name,
      phone: formData.phone,
      email: formData.email || null,
      address: formData.address,
      city: titleCase(formData.city || ""),
      apartment_details: formData.apartment_details || null,
      order_notes: formData.order_notes || null,
      payment_type: selectedOption,

      cart_id: cartId || null,

      coupon_id: couponId,
      coupon_code: couponCode,
      discount_percent: discountPercent,
      discount: Number(discountAmount.toFixed(2)),

      subtotal: Number(subtotal.toFixed ? subtotal.toFixed(2) : toNum(subtotal).toFixed(2)),
      delivery: Number(deliveryNum.toFixed ? deliveryNum.toFixed(2) : toNum(deliveryNum).toFixed(2)),
      total:    totalNum,

      items: itemsPayload,
    };

    try {
      await axios.post("https://laravel-api.aj-mart.com/api/orders/add", orderData, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      // تفريغ السلة (محلي + سيرفر)
      localStorage.removeItem("aj_cart");
      localStorage.removeItem("aj_checkout_snapshot");
      window.dispatchEvent(new Event("cart:cleared"));
      await clearServerCart();

      try {
        const res = await axios.get("https://laravel-api.aj-mart.com/cart", { withCredentials: true });
        const cart = res.data?.cart || {};
        const keysLeft = Object.keys(cart);
        if (keysLeft.length) {
          await clearServerCart();
        }
      } catch {}

      setCtx({
        cartId: null,
        cartItems: [],
        subtotal: 0,
        delivery: DEFAULT_DELIVERY,
        discountPercent: 0,
        discountAmount: 0,
        total: 0,
        couponCode: null,
        couponId: null,
      });
      setFormData({
        full_name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        apartment_details: "",
        order_notes: "",
      });
      setSelectedOption("");
      setPopup("Order created successfully", "success", 2500);
      navigate(".", { replace: true, state: {} });
    } catch (error) {
      setPopup(error?.response?.data?.message || "حدث خطأ أثناء تقديم الطلب", "error", 3000);
    } finally {
      setIsPlacing(false);
    }
  };

  return (
    <>
      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-[360px] flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white ${popupType === "success" ? "bg-green-500" : "bg-red-500"}`}>
              {popupType === "success" ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${popupType === "success" ? "text-green-700" : "text-red-700"}`}>{popupMessage}</p>
            </div>
            <button onClick={() => setShowPopup(false)} className="text-gray-500 hover:text-gray-700 focus:outline-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="landingPages">
        <div className="container h-full flex flex-col gap-2 justify-center">
          <h1 className="text-[32px] md:text-[36px] font-bold text-white">{t("Checkout")}</h1>
          <div className="flex items-center gap-1.5 text-white text-sm">
            <Link to={"/"} className="underline/10 hover:underline">{t("Home")}</Link>
            <span>—</span>
            <span>{t("Checkout")}</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-8 md:py-10">
        <div className="container grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Billing form */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold mb-4">{t("Billing Details")}</h2>

            <div className="grid grid-cols-1 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Full Name")} *</label>
                <input className="outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30" type="text" name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder={t("Full Name")} required />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Phone Number")} *</label>
                <input className="outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30" type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t("Phone Number")} required />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Town/City")} *</label>
                <select className="outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30" name="city" value={formData.city} onChange={handleInputChange} required>
                  <option value="" disabled>{t("Select Town/City")}</option>
                  <option value="Amman">{t("Amman")}</option>
                  <option value="Zarqa">{t("Zarqa")}</option>
                  <option value="Irbid">{t("Irbid")}</option>
                  <option value="Ajloun">{t("Ajloun")}</option>
                  <option value="Jerash">{t("Jerash")}</option>
                  <option value="Mafraq">{t("Mafraq")}</option>
                  <option value="Balqa">{t("Balqa")}</option>
                  <option value="Karak">{t("Karak")}</option>
                  <option value="Tafileh">{t("Tafileh")}</option>
                  <option value="Maan">{t("Maan")}</option>
                  <option value="Aqaba">{t("Aqaba")}</option>
                  <option value="Madaba">{t("Madaba")}</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Street Address")} *</label>
                <input className="outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30" type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder={t("Street Address")} required />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Apartment, floor, etc. (optional)")}</label>
                <input className="outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30" type="text" name="apartment_details" value={formData.apartment_details} onChange={handleInputChange} placeholder={t("Apartment, floor, etc. (optional)")} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Email Address")}</label>
                <input className="outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={t("Email Address")} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-gray-700 font-medium">{t("Order Notes (optional)")}</label>
                <textarea className="h-[180px] outline-none border border-gray-300 py-2.5 px-3 rounded-md focus:ring-2 focus:ring-[#1485A6]/30 resize-y" name="order_notes" value={formData.order_notes} onChange={handleInputChange} placeholder={t("Order Notes (optional)")} />
              </div>
            </div>
          </div>

          {/* Summary + Items */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 md:p-6">
              <h2 className="text-xl md:text-2xl font-bold mb-4">{t("Your Order")}</h2>

              <div className="space-y-3">
                {ctx.cartItems?.length ? (
                  ctx.cartItems.map((item, idx) => {
                    const isProduct = !!item.product_id;
                    const nameKey = isProduct ? "product_name_E" : "accessories_name_E";
                    const imageKey = isProduct ? "product_main_img" : "accessories_main_img";

                    const { unit } = computeUnitPrice(item);
                    const lineTotal = (unit * (Number(item.quantity) || 0)).toFixed(2);

                    const colorName = getColorName(item); // اسم فقط
                    const capacity = getCapacity(item);

                    // Models
                    const models = (() => {
                      const v =
                        item?.selected_models ??
                        item?.item_data?.selected_models ??
                        item?.models ??
                        null;
                      if (!v) return [];
                      if (Array.isArray(v)) return v;
                      if (typeof v === "string") {
                        try { const j = JSON.parse(v); if (Array.isArray(j)) return j; } catch {}
                        return v.split(",").map(s => s.trim()).filter(Boolean);
                      }
                      return [];
                    })();

                    return (
                      <div key={item.cart_key || idx} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <img
                            className="w-14 h-14 rounded-md border border-gray-200 object-contain bg-white"
                            src={`https://laravel-api.aj-mart.com/storage/app/public/${item[imageKey]}`}
                            alt={item[nameKey]}
                          />
                          <div className="flex flex-col min-w-0">
                            <div className="font-semibold text-sm truncate">{item[nameKey]}</div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                              {colorName && (
                                <span className="inline-flex items-center gap-1">
                                  <span className="font-medium">{t("Color")}:</span>
                                  <span className="inline-block rounded-full border px-1.5 py-[1px] bg-gray-50">{colorName}</span>
                                </span>
                              )}
                              {capacity && (
                                <span className="inline-flex items-center gap-1">
                                  <span className="font-medium">{t("Capacity")}:</span>
                                  <span className="inline-block rounded-full border px-1.5 py-[1px] bg-gray-50">{capacity}</span>
                                </span>
                              )}
                              {models.length > 0 && (
                                <span className="inline-flex items-center gap-1">
                                  <span className="font-medium">{t("Models")}:</span>
                                  <span className="flex flex-wrap gap-1">
                                    {models.map((m, i) => (
                                      <span key={i} className="inline-block rounded-full border px-1.5 py-[1px] bg-gray-50">
                                        {m}
                                      </span>
                                    ))}
                                  </span>
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1">
                                <span className="font-medium">{t("Qty")}:</span>
                                <span>{item.quantity}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">{toMoney(unit)} {t("JOD")}</div>
                          <div className="font-semibold">{lineTotal} {t("JOD")}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-gray-500">{t("Your cart is empty")}</div>
                )}
              </div>

              <div className="my-4 h-px bg-gray-200" />

              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t("Subtotal")}:</span>
                  <span className="font-medium">{toMoney(computedSubtotal)} {t("JOD")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t("Delivery")}:</span>
                  <span className="font-medium">{toMoney(deliveryVal)} {t("JOD")}</span>
                </div>
                {Number(ctx.discountAmount) > 0 && (
                  <div className="flex items-center justify-between text-red-600">
                    <span>{t("Discount")} {ctx.discountPercent ? `(${ctx.discountPercent}%)` : ""}:</span>
                    <span>-{toMoney(ctx.discountAmount)} {t("JOD")}</span>
                  </div>
                )}
                {ctx.couponCode && (
                  <div className="flex items-center justify-between text-sm text-emerald-700">
                    <span>{t("Coupon")}:</span>
                    <span className="font-semibold">{ctx.couponCode}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t font-semibold">
                  <span>{t("Total")}:</span>
                  <span>{toMoney(total)} {t("JOD")}</span>
                </div>
              </div>

              {/* الدفع */}
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="radio" value="CliQ" checked={selectedOption === "CliQ"} onChange={handleRadioChange} className="peer sr-only" disabled={orderPlaced || isPlacing} />
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOption === "CliQ" ? "border-[#1485A6]" : "border-gray-400"}`}>
                      {selectedOption === "CliQ" && <span className="w-2.5 h-2.5 rounded-full bg-[#1485A6]" />}
                    </span>
                    <span className="text-sm">{t("CliQ")}</span>
                  </label>
                  <img className="w-6 h-6 object-contain" src={require("../../assets/images/footer-pay-1.svg").default} alt="CliQ" />
                </div>

                {selectedOption === "CliQ" && !orderPlaced && (
                  <div className="rounded-lg border border-[#1485A6]/40 bg-[#1485A6]/5 p-3 text-sm space-y-1">
                    <div className="font-semibold">{t("CliQ Terms & Conditions")}</div>
                    <div>{t("You can pay by clicking on the following number or nickname")}:</div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold direction-ar">Zain Cash — (079 077 3377)</span>
                      <span className="font-semibold">Ahli Bank — (AJMART)</span>
                      <span className="font-semibold">{t("Abdallah Aljuneidi")}</span>
                    </div>
                    <div>{t("cliq content")}</div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="radio" value="Cash on Delivery" checked={selectedOption === "Cash on Delivery"} onChange={handleRadioChange} className="peer sr-only" disabled={orderPlaced || isPlacing} />
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOption === "Cash on Delivery" ? "border-[#1485A6]" : "border-gray-400"}`}>
                      {selectedOption === "Cash on Delivery" && <span className="w-2.5 h-2.5 rounded-full bg-[#1485A6]" />}
                    </span>
                    <span className="text-sm">{t("Cash on delivery")}</span>
                  </label>
                  <img className="w-6 h-6 object-contain" src={require("../../assets/images/footer-pay-2.svg").default} alt="COD" />
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={handlePlaceOrder}
                  className="w-full sm:w-auto px-5 py-2.5 bg-[#1485A6] text-white font-semibold rounded-md hover:opacity-90 transition disabled:opacity-50"
                  disabled={orderPlaced || isPlacing}
                >
                  {isPlacing ? t("Processing...") : t("Place Order")}
                </button>

                <a href="https://wa.link/ppk1jv" target="_blank" rel="noreferrer" className="w/full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md" style={{ background: "#29A71A", color: "#fff" }}>
                  <img className="w-5 h-5" src={require("../../assets/icons/whatsappWhite.svg").default} alt="WhatsApp" />
                  <span className="font-semibold">{t("Order via WhatsApp")}</span>
                </a>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 md:p-6">
              <h3 className="text-xl font-bold mb-3">{t("Delivery & Return")}</h3>
              <Faqs />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
